import { useMutation } from "@apollo/client";
import { useCallback } from "react";
import { FormattedMessage } from "react-intl";

import AddParentBlock from "@components/automations/AddParentBlock";
import styles from "@components/automations/Automation.module.css";
import AutomationActionBlockForm from "@components/automations/forms/AutomationActionBlockForm";
import TertiaryButton from "@components/buttons/TertiaryButton";
import BodyCopy from "@components/typography/BodyCopy";
import Heading3 from "@components/typography/Heading3";
import {
  AutomationActionBlockDestroyDocument,
  AutomationActionBlockDestroyMutation,
  AutomationActionBlockDestroyMutationVariables,
  AutomationFragment as AutomationType
} from "@typing/Generated";

type AutomationActionBlock = Extract<AutomationType["blocks"][number], { __typename: "AutomationActionBlock" }>;

type Block = NonNullable<AutomationType["blocks"][number]>;

type Props = {
  automation: AutomationType;
  automationActionBlock: AutomationActionBlock;
  onBlockAdded: (block: Block) => void;
};

const AutomationActionBlockPanel = ({ automation, automationActionBlock, onBlockAdded }: Props) => {
  const [destroyAutomationActionBlock] = useMutation<
    AutomationActionBlockDestroyMutation,
    AutomationActionBlockDestroyMutationVariables
  >(AutomationActionBlockDestroyDocument);

  const handleDestroyBlock = useCallback(() => {
    destroyAutomationActionBlock({ variables: { id: automationActionBlock.id } });
  }, [automationActionBlock.id, destroyAutomationActionBlock]);

  return (
    <>
      <Heading3>
        <FormattedMessage id="models.automation.actionBlock" />
      </Heading3>
      {automationActionBlock.actionBrokenReason && (
        <div className={styles.brokenAutomationExplanation}>
          <BodyCopy color="danger-500">
            <FormattedMessage
              id={`models.automationActionBlock.brokenReasons.${automationActionBlock.actionBrokenReason}`}
            />
          </BodyCopy>
        </div>
      )}
      {!automation.locked && (
        <TertiaryButton icon="trash" onClick={handleDestroyBlock} textKey={"models.automation.deleteBlock"} />
      )}
      <AddParentBlock automation={automation} childBlock={automationActionBlock} onBlockAdded={onBlockAdded} />
      <AutomationActionBlockForm automationActionBlock={automationActionBlock} locked={automation.locked} />
    </>
  );
};

export default AutomationActionBlockPanel;
